import { normalizeDistrictLabel } from './districtDisplay';

/**
 * Reading a respondent's district, whichever shape it was stored in.
 *
 * `demographics.district` holds five parts — us_house, state_senate,
 * state_house, county, municipality — but it has been written two ways:
 *
 *   object  a real nested object (what the address lookup returns)
 *   string  that object run through JSON.stringify before saving
 *
 * The string form came from PublicOutreachPage, which stringified the lookup
 * result into the demographic value. Two consumers then JSON.parse'd it back,
 * so the round trip worked and nobody noticed — until district became a
 * reportable field and the reporting reads went looking for `.state_house` on
 * what was actually a string.
 *
 * The writer now stores the object. This exists because five months of
 * responses are already stringified and must keep working, and because a
 * consumer should never have to care which one it got.
 */

export interface Districts {
  us_house?: string | null;
  state_senate?: string | null;
  state_house?: string | null;
  county?: string | null;
  municipality?: string | null;
}

/**
 * Normalize a stored district value to an object.
 *
 * Returns null for anything unreadable — absent, empty, or a string that is
 * not valid JSON — so a single malformed row degrades to "no district" rather
 * than throwing inside a table render.
 */
export function parseDistricts(value: unknown): Districts | null {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'object') return value as Districts;

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? (parsed as Districts) : null;
    } catch {
      return null;
    }
  }

  return null;
}

/**
 * Read one part out of a stored district value, whichever shape it is in.
 * `part` is a key of Districts — 'state_house', 'us_house', …
 */
export function readDistrictPart(value: unknown, part: string): string | null {
  const districts = parseDistricts(value);
  if (!districts) return null;
  const found = (districts as Record<string, unknown>)[part];
  return found === null || found === undefined || found === '' ? null : String(found);
}

/** Human-readable summary, e.g. "US HD-02, CO SD-17, Boulder County" (canonical labels, 2026-08-27). */
export function formatDistricts(value: unknown): string {
  const d = parseDistricts(value);
  if (!d) return '';

  const parts: string[] = [];
  if (d.us_house) parts.push(normalizeDistrictLabel(d.us_house));
  if (d.state_senate) parts.push(normalizeDistrictLabel(d.state_senate));
  if (d.state_house) parts.push(normalizeDistrictLabel(d.state_house));
  if (d.county) parts.push(String(d.county));
  if (d.municipality) parts.push(String(d.municipality));
  return parts.join(', ');
}
